import type { URLSearchParamsInit } from 'react-router';
import { createSearchParams } from 'react-router';

/** Repeatable query key for additional playground evaluations (path param is primary). */
export const PLAYGROUND_COMPARE_EVALUATION_QUERY_KEY = 'compare';

export function parsePlaygroundCompareEvaluationIds(searchParams: URLSearchParams): string[] {
  return searchParams
    .getAll(PLAYGROUND_COMPARE_EVALUATION_QUERY_KEY)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Search params containing only `compare` entries (for merging onto a base URL). */
export function buildPlaygroundCompareSearchParams(compareEvaluationIds: readonly string[]): URLSearchParams {
  const next = new URLSearchParams();
  for (const raw of compareEvaluationIds) {
    const id = raw.trim();
    if (id) {
      next.append(PLAYGROUND_COMPARE_EVALUATION_QUERY_KEY, id);
    }
  }
  return next;
}

/**
 * Returns a copy of `base` with every `compare` entry removed, then appends the given ids in order.
 */
export function mergePlaygroundCompareIntoSearchParams(
  base: URLSearchParamsInit | undefined,
  compareEvaluationIds: readonly string[],
): URLSearchParams {
  const next = new URLSearchParams(createSearchParams(base ?? ''));
  next.delete(PLAYGROUND_COMPARE_EVALUATION_QUERY_KEY);
  for (const raw of compareEvaluationIds) {
    const id = raw.trim();
    if (id) {
      next.append(PLAYGROUND_COMPARE_EVALUATION_QUERY_KEY, id);
    }
  }
  return next;
}

/**
 * Primary id from the path first, then `compare` query values in order, skipping duplicates
 * (primary always wins; later repeats are dropped).
 */
export function orderedPlaygroundEvaluationIds(primaryEvaluationId: string, searchParams: URLSearchParams): string[] {
  const primary = primaryEvaluationId.trim();
  if (!primary) {
    return [];
  }
  const seen = new Set<string>([primary]);
  const out = [primary];
  for (const id of parsePlaygroundCompareEvaluationIds(searchParams)) {
    if (seen.has(id)) {
      continue;
    }
    seen.add(id);
    out.push(id);
  }
  return out;
}
